import { useEffect, useRef, useMemo, useState, useCallback } from "react";
import * as Sentry from "@sentry/react";
import useGlobalStore from "../../../../store/global.store";
import { useRemote } from "../../../../hooks/useRemote";
import { useTV } from "../../../../hooks/useTV";
import { globalStyles } from "../../../../utils/global-styles";
import { useConfig } from "../../../../hooks/useConfig";
import moment from "moment-timezone";
import VipSubCheck from "../VipSubCheck";
import axios from "../../../../config/axios";
import VodGalleryTV from "./VodGalleryTV";
import VodGalleryDisplay from "./VodGalleryDisplay";

const VipPiano = ({ device }) => {
  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  const globalDevice = useGlobalStore((state) => state.device);
  const { updateView } = useRemote();
  const { isTv } = useTV();
  const { config } = useConfig();

  // Release flag: config/Buttons.Episodes (+ Episodes_Override [subIds]).
  // Absent node/flag = hidden — same system as the bottom-bar buttons.
  const episodesEnabled = (() => {
    const b = config?.Buttons;
    if (!b) return false;
    if (b.Episodes === true) return true;
    const ov = b.Episodes_Override;
    const id = globalDevice?.subscription?._id;
    if (!id) return false;
    if (Array.isArray(ov)) return ov.includes(id);
    if (typeof ov === "string") return ov === id;
    return false;
  })();
  const pianoChoice = Number(config?.pianostreamURL);
  const pianoLiveSrc =
    pianoChoice === 1
      ? isTv
        ? "https://rtmp3.mywaitserver.com/live/player/1080p/"
        : "https://rtmp3.mywaitserver.com/live/player/720p/"
      : pianoChoice === 2
      ? isTv
        ? "https://rtmp2.mywaitserver.com/live/player/1080p/"
        : "https://rtmp2.mywaitserver.com/live/player/720p/"
      : pianoChoice === 3
      ? isTv
        ? "https://rtmp.mywaitserver.com/live/player/"
        : "https://rtmp.mywaitserver.com/live/player/"
      : isTv
      ? "https://rtmp2.mywaitserver.com/live/player/1080p/" // default fallback
      : "https://rtmp2.mywaitserver.com/live/player/720p/";

  // Use device prop if provided, otherwise fall back to global store
  const currentDevice = device || globalDevice;

  const getSavedVolume = () => {
    const raw = localStorage.getItem("volume");
    const v = parseFloat(raw ?? "1");
    return isNaN(v) ? 1 : Math.max(0, Math.min(1, v));
  };

  const pianoAudioRef = useRef(null);
  const liveExitRef = useRef(null);
  const splashExitRef = useRef(null);
  const splashWatchRef = useRef(null);

  const [focusedButton, setFocusedButton] = useState("exit");
  const [livePianoRefreshValue, setLivePianoRefreshValue] = useState(null);
  const [showVodTest, setShowVodTest] = useState(false);
  // ----- VOD GALLERY: full on-demand page (replaces splash while open) -----
  const [showVodGallery, setShowVodGallery] = useState(false);
  // true while a VOD video is actually playing inside the gallery
  const [vodVideoActive, setVodVideoActive] = useState(false);

  const isShowLive = config?.Piano_Show_On === true;

  const handleExitClick = useCallback(() => {
    updateView("VIP");
  }, [updateView]);

  useEffect(() => {
    if (config?.Live_Piano_Refresh_Value) {
      setLivePianoRefreshValue(config?.Live_Piano_Refresh_Value);

      if (
        livePianoRefreshValue !== null &&
        livePianoRefreshValue !== config?.Live_Piano_Refresh_Value
      ) {
        console.log("livePianoRefreshValue", livePianoRefreshValue);
        console.log(
          "config?.Live_Piano_Refresh_Value",
          config?.Live_Piano_Refresh_Value
        );
        localStorage.setItem("memClear", "yes");
        window.location.reload();
      }
    }
  }, [config?.Live_Piano_Refresh_Value, livePianoRefreshValue]);

  // API call to increment viewer count when component mounts
  useEffect(() => {
    if (currentDevice?._id) {
      axios
        .post("/live-piano/increment-viewer-count", {
          device_id: currentDevice._id,
        })
        .catch((error) => {
          console.error("Failed to increment viewer count:", error);
          Sentry.captureException(error);
        });
    }

    // Cleanup function to decrement viewer count when component unmounts
    return () => {
      if (currentDevice?._id) {
        axios
          .post("/live-piano/decrement-viewer-count", {
            device_id: currentDevice._id,
          })
          .catch((error) => {
            console.error("Failed to decrement viewer count:", error);
            Sentry.captureException(error);
          });
      }
    };
  }, [currentDevice?._id]);

  useEffect(() => {
    setShowBottomBar(false);
    try {
      if (pianoAudioRef.current) {
        const vol = getSavedVolume();
        pianoAudioRef.current.volume = vol;
        if (!isShowLive && vol > 0) {
          pianoAudioRef.current.play().catch(() => {});
        }
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [device, setShowBottomBar, isShowLive]);

  // ----- AUDIO: pause only for the live show or while a VOD video plays;
  //       keeps playing through splash <-> gallery transitions -----
  useEffect(() => {
    try {
      const el = pianoAudioRef.current;
      if (!el) return;
      if (isShowLive || vodVideoActive) {
        el.pause();
      } else {
        const vol = getSavedVolume();
        el.volume = vol;
        if (vol > 0) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [isShowLive, vodVideoActive]);

  // ----- VOD TEST: pause splash audio while the test player is open -----
  useEffect(() => {
    const el = pianoAudioRef.current;
    if (!el) return;
    if (showVodTest) {
      el.pause();
    } else if (!isShowLive) {
      const vol = getSavedVolume();
      if (vol > 0) el.play().catch(() => {});
    }
  }, [showVodTest, isShowLive]);

  // ----- VOD TEST: remote SELECT closes the test player on TV -----
  useEffect(() => {
    if (!(isTv && showVodTest)) return;
    const handleKeyDown = (e) => {
      const kc = e.keyCode;
      const isSelect =
        e.key === "Enter" ||
        e.key === "OK" ||
        e.key === "Select" ||
        kc === 13 ||
        kc === 23 ||
        kc === 66;
      if (isSelect) {
        e.preventDefault();
        e.stopPropagation();
        setShowVodTest(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isTv, showVodTest]);

  useEffect(() => {
    if (isTv && isShowLive) {
      Promise.resolve().then(() => {
        liveExitRef.current?.focus();
        setFocusedButton("exit");
      });
    }
  }, [isTv, isShowLive]);

  useEffect(() => {
    if (!(isTv && isShowLive)) return;
    const handleKeyDown = (e) => {
      const kc = e.keyCode;
      const isSelect =
        e.key === "Enter" ||
        e.key === "OK" ||
        e.key === "Select" ||
        kc === 13 ||
        kc === 23 ||
        kc === 66;
      if (isSelect) {
        e.preventDefault();
        e.stopPropagation();
        handleExitClick();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isTv, isShowLive, handleExitClick]);

  useEffect(() => {
    const isShowingSplash = isTv && !isShowLive && !showVodGallery;
    if (isShowingSplash) {
      Promise.resolve().then(() => {
        splashExitRef.current?.focus();
        setFocusedButton("exit");
      });
    }
  }, [isTv, isShowLive, showVodGallery]);

  // ----- SPLASH: remote nav — UP focuses the neon sign, DOWN returns to EXIT,
  //       SELECT fires whichever is focused -----
  useEffect(() => {
    const isShowingSplash = isTv && !isShowLive && !showVodGallery;
    if (!isShowingSplash || showVodTest) return;
    const handleKeyDown = (e) => {
      const kc = e.keyCode;
      const isUp = e.key === "ArrowUp" || kc === 38;
      const isDown = e.key === "ArrowDown" || kc === 40;
      const isSelect =
        e.key === "Enter" ||
        e.key === "OK" ||
        e.key === "Select" ||
        kc === 13 ||
        kc === 23 ||
        kc === 66;

      if (isUp || isDown) {
        e.preventDefault();
        e.stopPropagation();
        // up from EXIT -> neon sign; down from neon sign -> EXIT
        setFocusedButton((prev) => {
          // Neon sign may be flag-hidden — never navigate to it then.
          const next = isUp && episodesEnabled ? "watch" : "exit";
          if (next === "watch") splashWatchRef.current?.focus();
          else splashExitRef.current?.focus();
          return next;
        });
      } else if (isSelect) {
        e.preventDefault();
        e.stopPropagation();
        if (focusedButton === "watch") {
          // ----- VOD GALLERY: neon sign opens the on-demand page -----
          setShowVodGallery(true);
        } else {
          handleExitClick();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isTv, isShowLive, showVodTest, showVodGallery, focusedButton, handleExitClick, episodesEnabled]);

  const getButtonStyles = (buttonId, baseStyle) => {
    if (isTv && focusedButton === buttonId) {
      return {
        ...baseStyle,
        color: "white",
        borderColor: "#3ADF3C",
        outline: "none",
        border: "6px solid #3ADF3C",
      };
    }
    return baseStyle;
  };

  const nextShowText = useMemo(() => {
    const shows = config?.pianoShows;
    if (!Array.isArray(shows) || shows.length === 0) return null;
    const nowET = moment.tz("America/New_York");
    const parsed = shows
      .map((s) => {
        if (!s?.date || !s?.time) return null;
        return moment.tz(
          `${s.date} ${s.time}`,
          "YYYY-MM-DD HH:mm",
          "America/New_York"
        );
      })
      .filter((m) => m?.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf());
    if (parsed.length === 0) return null;
    const next =
      parsed.find((m) => m.valueOf() >= nowET.valueOf()) ||
      parsed[parsed.length - 1];
    const dateStr = next.format("MMMM D, YYYY");
    const timeStr = next.format("h:mm A");
    return `${dateStr} @ ${timeStr} (EST)`;
  }, [config?.pianoShows]);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "#380a01",
      }}
    >
      {/* ----- AUDIO: mounted at all times (splash AND gallery) so the music
             continues across transitions; effects above pause/resume it ----- */}
      <audio
        src="https://cdn.mywaitserver.live/pianomusic1.mp3"
        ref={pianoAudioRef}
        loop
      />
      {isShowLive ? (
        <>
          <iframe
            title="Piano Live"
            src={pianoLiveSrc}
            allow="autoplay; fullscreen"
            allowFullScreen
            style={{
              position: "absolute",
              inset: 0,
              width: "100vw",
              height: "100vh",
              border: "none",
              pointerEvents: "none",
            }}
          />
          <button
            ref={liveExitRef}
            onClick={handleExitClick}
            onContextMenu={(e) => e.preventDefault()}
            onFocus={() => setFocusedButton("exit")}
            onBlur={() => setFocusedButton(null)}
            tabIndex={0}
            style={getButtonStyles("exit", {
              ...globalStyles.button(isTv),
              position: "absolute",
              zIndex: 2000,
              right: isTv ? 20 : 16,
              bottom: isTv ? 20 : 16,
              backgroundColor: "rgba(0,0,0,0.35)",
              height: isTv ? "70px" : "50px",
              fontSize: "30px",
              borderColor: "white",
              ...(isTv ? { marginRight: "40px" } : {}),
            })}
          >
            <span style={{ color: "white" }}>EXIT</span>
          </button>
        </>
      ) : showVodGallery ? (
        /* ----- VOD GALLERY: on-demand page (replaces splash while open).
           If a live show starts, isShowLive wins the outer ternary and
           the live iframe takes over automatically, exactly as before. ----- */
        isTv ? (
          <VodGalleryTV
            onExit={() => setShowVodGallery(false)}
            onVideoActive={setVodVideoActive}
          />
        ) : (
          <VodGalleryDisplay
            onExit={() => setShowVodGallery(false)}
            onVideoActive={setVodVideoActive}
          />
        )
      ) : (
        <>
          <video
            src={
              isTv
                ? "https://cdn.mywaitserver.live/piano.mp4"
                : "https://cdn.mywaitserver.live/pianodisplay.mp4"
            }
            autoPlay
            loop
            muted
            style={{ width: "100vw", height: "100vh" }}
          />
          {/* ----- WATCH PAST EPISODES: neon sign, centered above bottom bar ----- */}
          {episodesEnabled && (
          <button
            ref={splashWatchRef}
            onClick={() => setShowVodGallery(true)}
            onContextMenu={(e) => e.preventDefault()}
            onFocus={() => setFocusedButton("watch")}
            onBlur={() => setFocusedButton(null)}
            tabIndex={0}
            className={isTv && focusedButton === "watch" ? "vodNeonBtnFocused" : "vodNeonBtn"}
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              bottom: isTv ? 130 : 95,
              zIndex: 1100,
              background:
                "radial-gradient(120% 160% at 50% 50%, rgba(20,6,2,.85) 0%, rgba(20,6,2,.55) 60%, rgba(20,6,2,0) 100%)",
              border: isTv && focusedButton === "watch"
                ? "4px solid #3ADF3C"
                : "4px solid #bfefff",
              boxShadow: isTv && focusedButton === "watch"
                ? "0 0 6px rgba(58,223,60,1), 0 0 18px rgba(58,223,60,.9), 0 0 40px rgba(58,223,60,.6), 0 0 80px rgba(58,223,60,.35), inset 0 0 14px rgba(58,223,60,.35)"
                : undefined,
              borderRadius: 18,
              padding: isTv ? "22px 48px" : "14px 28px",
              cursor: "pointer",
              fontFamily: "Lato",
              fontWeight: 900,
              fontSize: isTv ? 36 : 25,
              letterSpacing: 2.5,
              color: "#ffffff",
              outline: "none",
            }}
          >
            <span style={{ textShadow: "0 0 4px rgba(255,255,255,.9), 0 0 10px rgba(255,255,255,.4)" }}>
              WATCH PAST EPISODES!
            </span>
          </button>
          )}
          <style>{`
            /* blue neon tube: strong steady glow, and IT does the flicker */
            .vodNeonBtn{
              box-shadow:
                0 0 6px rgba(191,239,255,1),
                0 0 18px rgba(122,220,255,.9),
                0 0 40px rgba(57,182,255,.65),
                0 0 80px rgba(57,182,255,.35),
                inset 0 0 14px rgba(57,182,255,.35);
              animation: vodTubeFlicker 6s infinite steps(60);
            }
            @keyframes vodTubeFlicker{
              0%, 84%, 100%{
                box-shadow:
                  0 0 6px rgba(191,239,255,1),
                  0 0 18px rgba(122,220,255,.9),
                  0 0 40px rgba(57,182,255,.65),
                  0 0 80px rgba(57,182,255,.35),
                  inset 0 0 14px rgba(57,182,255,.35);
              }
              85%, 89%, 94%{
                box-shadow:
                  0 0 3px rgba(191,239,255,.4),
                  0 0 8px rgba(122,220,255,.3),
                  inset 0 0 6px rgba(57,182,255,.15);
              }
              87%, 92%, 96%{
                box-shadow:
                  0 0 6px rgba(191,239,255,1),
                  0 0 18px rgba(122,220,255,.9),
                  0 0 40px rgba(57,182,255,.65),
                  0 0 80px rgba(57,182,255,.35),
                  inset 0 0 14px rgba(57,182,255,.35);
              }
            }
            /* green neon tube when focused by the TV remote */
            .vodNeonBtnFocused{
              box-shadow:
                0 0 6px rgba(58,223,60,1),
                0 0 18px rgba(58,223,60,.9),
                0 0 40px rgba(58,223,60,.6),
                0 0 80px rgba(58,223,60,.35),
                inset 0 0 14px rgba(58,223,60,.35);
              transform: translateX(-50%) scale(1.12) !important;
              transition: transform .15s ease;
            }
          `}</style>
          <div
            style={{
              position: "absolute",
              bottom: "0px",
              left: "10px",
              zIndex: 1000,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "10px",
              width: "100%",
              padding: isTv ? "10px 20px" : "0px 20px",
            }}
          >
            <span
              style={{
                fontSize: isTv ? "50px" : "33px",
                color: "white",
                fontWeight: "bold",
                fontFamily: "Lato",
              }}
            >
              As seen in Disney Springs, Universal City Walk, & Jelly Rolls!
            </span>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "10px",
              }}
            >

              <button
                ref={splashExitRef}
                onClick={handleExitClick}
                onContextMenu={(e) => e.preventDefault()}
                onFocus={() => setFocusedButton("exit")}
                onBlur={() => setFocusedButton(null)}
                tabIndex={0}
                style={getButtonStyles("exit", {
                  ...globalStyles.button(isTv),
                  borderColor: "white",
                  backgroundColor: "transparent",
                  height: isTv ? "70px" : "50px",
                  fontSize: "30px",
                  ...(isTv ? { marginRight: "40px" } : {}),
                })}
              >
                <span style={{ color: "white" }}>EXIT</span>
              </button>
            </div>
          </div>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              backgroundColor: "transparent",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              padding: "0px 20px",
              paddingTop: isTv ? "30px" : "20px",
            }}
          >
            <span
              style={{
                fontSize: isTv ? "55px" : "38px",
                fontWeight: "bold",
                fontFamily: "Lato",
                color: "white",
              }}
            >
              Next Live Show: {nextShowText ?? "TBA"}
            </span>
          </div>
        </>
      )}

      {showVodTest && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "#000",
            zIndex: 3000,
          }}
        >
          <iframe
            title="VOD Player"
            src={
              isTv
                ? "https://vod.mywaitserver.live/player5.html?ui=tv&src=https://vod.mywaitserver.live/show_final/stream_1080p.m3u8"
                : "https://vod.mywaitserver.live/player5.html?src=https://vod.mywaitserver.live/show_final/stream_720p.m3u8"
            }
            allow="autoplay; fullscreen"
            allowFullScreen
            style={{
              position: "absolute",
              inset: 0,
              width: "100vw",
              height: "100vh",
              border: "none",
            }}
          />
          <button
            onClick={() => setShowVodTest(false)}
            onFocus={() => setFocusedButton("vodexit")}
            tabIndex={0}
            style={{
              ...globalStyles.button(isTv),
              position: "absolute",
              zIndex: 3100,
              right: isTv ? 20 : 16,
              bottom: isTv ? 20 : 16,
              backgroundColor: "rgba(0,0,0,0.5)",
              height: isTv ? "70px" : "50px",
              fontSize: "30px",
              borderColor: "white",
              color: "white",
              ...(isTv ? { marginRight: "40px" } : {}),
            }}
          >
            EXIT TEST
          </button>
        </div>
      )}

      <VipSubCheck />
    </div>
  );
};

export default VipPiano;
